import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Inbox, Send, Sparkles, Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import OfferFormDialog from "@/components/offers/OfferFormDialog";
import { providerOffersApi } from "@/lib/api/offers";
import { providerApi } from "@/lib/api/provider";
import { categoriesApi } from "@/lib/api/categories";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

function StatusBadge({ status, t }) {
    const tone = status === "APPROVED" ? "border-success/40 text-success bg-success/5"
              : status === "PENDING"  ? "border-warning/40 text-warning bg-warning/5"
              : status === "REJECTED" ? "border-destructive/40 text-destructive bg-destructive/5"
              : "border-border text-muted-foreground";
    return <Badge variant="outline" className={tone}>{t(`offers.status.${status}`)}</Badge>;
}

function DiscountChip({ offer, t }) {
    if (offer.discount_type === "PERCENTAGE") {
        return <span className="text-2xl font-bold tracking-tight text-primary">{Math.round(offer.discount_value)}%</span>;
    }
    return (
        <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold tracking-tight text-primary">{Math.round(offer.discount_value)}</span>
            <span className="text-xs text-muted-foreground">{t("offers.egp")}</span>
            {offer.original_price && (
                <span className="text-xs text-muted-foreground line-through ms-1">{Math.round(offer.original_price)} {t("offers.egp")}</span>
            )}
        </div>
    );
}

export default function OffersPage() {
    const { t, lang } = useI18n();
    const [offers, setOffers] = useState(null);
    const [locations, setLocations] = useState([]);
    const [staff, setStaff] = useState([]);
    const [allL2Categories, setAllL2Categories] = useState([]);
    const [providerCategories, setProviderCategories] = useState([]);
    const [dialog, setDialog] = useState({ open: false, initial: null });
    const [confirmDel, setConfirmDel] = useState(null);
    const [submitting, setSubmitting] = useState(null);

    const refresh = async () => {
        try {
            const [list, locs, st, me, tree] = await Promise.all([
                providerOffersApi.list(),
                providerApi.listLocations(),
                providerApi.listStaff(),
                providerApi.me(),
                categoriesApi.listTree(false),
            ]);
            setOffers(list);
            setLocations((locs || []).filter((l) => l.is_active));
            setStaff((st || []).filter((s) => s.is_active));
            // Flatten unique L2 categories
            const flat = [];
            const seen = new Set();
            (tree || []).forEach((l1) => (l1.children || []).forEach((l2) => {
                if (!seen.has(l2.id)) { seen.add(l2.id); flat.push(l2); }
            }));
            setAllL2Categories(flat);
            // Restrict the offer category picker to the provider's linked specialties.
            const myCatIds = new Set(me.category_ids || []);
            setProviderCategories(flat.filter((c) => myCatIds.has(c.id)));
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };
    useEffect(() => { refresh(); }, []);

    const save = ({ id, payload }) =>
        id ? providerOffersApi.update(id, payload) : providerOffersApi.create(payload);

    const onSaved = () => refresh();

    const onSubmit = async (offer) => {
        setSubmitting(offer.id);
        try {
            await providerOffersApi.submit(offer.id);
            toast.success(t("offers.toasts.submitted"));
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(null);
        }
    };

    const onDelete = async () => {
        try {
            await providerOffersApi.remove(confirmDel.id);
            toast.success(t("offers.toasts.deleted"));
            setConfirmDel(null);
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };

    const titleFor = (o) => lang === "ar" ? (o.title_ar || o.title_en) : (o.title_en || o.title_ar);

    return (
        <div className="max-w-5xl space-y-6 fade-up" data-testid="page-provider-offers">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t("offers.providerTitle")}</h1>
                    <p className="mt-2 text-sm text-muted-foreground max-w-2xl">{t("offers.providerSubtitle")}</p>
                </div>
                <Button onClick={() => setDialog({ open: true, initial: null })} data-testid="add-offer-button">
                    <Plus className="h-4 w-4 me-1" />
                    {t("offers.add")}
                </Button>
            </header>

            {offers === null && (
                <div className="surface-card p-12 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
            )}
            {offers && offers.length === 0 && (
                <div className="surface-card p-10 text-center" data-testid="offers-empty">
                    <Inbox className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
                    <p className="text-sm font-medium">{t("offers.empty")}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{t("offers.emptyHint")}</p>
                </div>
            )}
            {offers && offers.length > 0 && (
                <div className="space-y-3">
                    {offers.map((offer) => (
                        <article
                            key={offer.id}
                            className="surface-card p-5 group"
                            data-testid={`offer-card-${offer.id}`}
                        >
                            <div className="flex items-start gap-4">
                                <div className="icon-tile h-12 w-12 shrink-0">
                                    <Sparkles className="h-5 w-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start gap-2 flex-wrap">
                                        <h3 className="text-base font-semibold truncate flex-1 min-w-0">{titleFor(offer)}</h3>
                                        <StatusBadge status={offer.status} t={t} />
                                    </div>
                                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                                        <DiscountChip offer={offer} t={t} />
                                        <span className="text-xs text-muted-foreground">
                                            {offer.start_date} → {offer.end_date}
                                        </span>
                                        {offer.is_live && (
                                            <Badge variant="outline" className="border-success/40 text-success text-[10px]">
                                                {t("offers.live")}
                                            </Badge>
                                        )}
                                    </div>
                                    {offer.rejection_reason && (
                                        <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                                            <strong>{t("offers.rejectionReason")}: </strong>{offer.rejection_reason}
                                        </div>
                                    )}
                                    <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                                        {offer.location_ids?.length > 0 && <span><Tag className="inline h-3 w-3 me-1" />{t("offers.scope.locations", { n: offer.location_ids.length })}</span>}
                                        {offer.staff_ids?.length > 0 && <span><Tag className="inline h-3 w-3 me-1" />{t("offers.scope.staff", { n: offer.staff_ids.length })}</span>}
                                        {offer.category_ids?.length > 0 && <span><Tag className="inline h-3 w-3 me-1" />{t("offers.scope.categories", { n: offer.category_ids.length })}</span>}
                                        {offer.location_ids?.length === 0 && offer.staff_ids?.length === 0 && offer.category_ids?.length === 0 && (
                                            <span>{t("offers.scope.all")}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1 shrink-0">
                                    {(offer.status === "DRAFT" || offer.status === "REJECTED") && (
                                        <Button
                                            size="sm"
                                            onClick={() => onSubmit(offer)}
                                            disabled={submitting === offer.id}
                                            data-testid={`offer-submit-${offer.id}`}
                                        >
                                            {submitting === offer.id ? <Loader2 className="h-4 w-4 me-1 animate-spin" /> : <Send className="h-4 w-4 me-1" />}
                                            {t("offers.submit")}
                                        </Button>
                                    )}
                                    {offer.status !== "PENDING" && (
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => setDialog({ open: true, initial: offer })}
                                            data-testid={`offer-edit-${offer.id}`}
                                            aria-label="Edit"
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                    )}
                                    {offer.status !== "APPROVED" && (
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => setConfirmDel(offer)}
                                            data-testid={`offer-delete-${offer.id}`}
                                            aria-label="Delete"
                                        >
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            <OfferFormDialog
                open={dialog.open}
                onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
                initial={dialog.initial}
                save={save}
                onSaved={onSaved}
                locations={locations}
                staff={staff}
                categories={providerCategories.length ? providerCategories : allL2Categories}
            />
            <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
                <AlertDialogContent data-testid="offer-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("offers.confirmDeleteTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("offers.confirmDeleteBody", { name: titleFor(confirmDel || {}) })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="offer-delete-confirm-button">
                            {t("offers.delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
